import { format } from "date-fns";

/**
 * Builds the per-tab query descriptors for AvailabilityCalendar.
 *
 * The "All sites" descriptor (index 0) has null seedKeys/siteIds so its
 * query key is the baseline facility-wide key — visiting one partner page
 * after another does not refetch.
 *
 * Each subsequent descriptor maps to a `siteGroup` from the loader.
 */
export function buildQueryDescriptors(siteGroups) {
  const all = { key: "all", seedKeys: null, siteIds: null };
  if (!Array.isArray(siteGroups) || siteGroups.length === 0) {
    return [all];
  }
  return [
    all,
    ...siteGroups.map((group) => ({
      key: group.name,
      seedKeys: group.seedKeys,
      siteIds: group.siteIds,
    })),
  ];
}

/**
 * Builds the deep-link CTA URL the calendar's "Watch these dates" button uses.
 *
 * Uses comma-separated, *unencoded* values to mirror the existing
 * `AvailabilityCalendar.jsx` pattern. Do NOT switch to URLSearchParams —
 * its `toString()` percent-encodes commas.
 *
 * `siteType` (when present on the active group) is passed through so the
 * `/new/...` form pre-selects the matching type-filter button. Without it,
 * the form's "Select from matching sites" list is empty until the user
 * picks a type — even though the IDs are seeded internally.
 */
export function buildCtaUrl({ facilitySlug, selectedDates, activeGroup }) {
  const parts = [];
  if (Array.isArray(selectedDates) && selectedDates.length > 0) {
    const dateStrings = selectedDates.map((d) => format(d, "yyyy-MM-dd"));
    parts.push(`dates=${dateStrings.join(",")}`);
  }
  if (
    activeGroup &&
    Array.isArray(activeGroup.siteIds) &&
    activeGroup.siteIds.length > 0
  ) {
    parts.push(`selectedSiteIds=${activeGroup.siteIds.join(",")}`);
    if (activeGroup.siteType) {
      parts.push(`siteType=${activeGroup.siteType}`);
    }
  }
  if (parts.length === 0) {
    return `/new/${facilitySlug}`;
  }
  return `/new/${facilitySlug}?${parts.join("&")}`;
}

/**
 * Resolves the active siteGroup object given the active tab index.
 *
 * Index 0 is the "All sites" tab and has no group; indices 1..n map to
 * `siteGroups[index - 1]`. Out-of-bounds, null, or invalid inputs return null.
 */
export function normalizeActiveGroup(activeGroupIndex, siteGroups) {
  if (!Array.isArray(siteGroups)) return null;
  if (typeof activeGroupIndex !== "number") return null;
  if (activeGroupIndex <= 0) return null;
  const groupIndex = activeGroupIndex - 1;
  if (groupIndex < 0 || groupIndex >= siteGroups.length) return null;
  return siteGroups[groupIndex];
}
